import React from 'react';
import heroImg from '../assets/bg-header.jpg';
import alainPortrait from '../assets/alain-portrait.jpeg';
import clinicsImg from '../assets/clinics.webp';
import privateLessonsImg from '../assets/private-lessons.jpg';
import groupLessonsImg from '../assets/group-lessons.jpg';
import alainPlay01 from '../assets/alain-play-01.jpeg';
import alainPlay02 from '../assets/alain-play-02.jpeg';
import alainServe from '../assets/alain-serve.jpeg';

const programs = [
  {
    title: 'Private Lessons',
    desc: 'Personalized coaching for all skill levels. Flexible scheduling and focused guidance.',
    img: privateLessonsImg,
    cta: 'Book Now',
    link: '/courses',
  },
  {
    title: 'Tennis Clinics',
    desc: 'Targeted sessions for specific skills. Master serves, forehands, backhands, and more.',
    img: clinicsImg,
    cta: 'Learn More',
    link: '/courses',
  },
  {
    title: 'Group Lessons',
    desc: 'Fun, social, and effective group training. Grow together and improve your game.',
    img: groupLessonsImg,
    cta: 'Join Group',
    link: '/courses',
  },
];

const testimonials = [
  {
    name: 'Maninder',
    text: `Coach Alain is an amazing tennis coach who knows their stuff and loves what he do. He is great at helping beginners get the basics and pushing more experienced players to up their game. What sets him apart is how he pay attention to the details, giving helpful feedback that really speeds up your progress. It's not just about skills – he also help you build a positive mindset, boosting your confidence. Off the court, he is professional, on time, and easy to communicate with. Whether you're just starting or looking to improve, Coach Alain is the go-to person for a top-notch tennis coaching experience. I'd give them a solid 5/5 – highly recommend!`,
  },
  {
    name: 'Giuseppe',
    text: `Coach Alain, your coaching is truly transformative. Your passion for tennis is contagious, and your ability to simplify complex techniques has accelerated my progress. Your personalized attention and positive approach boost my confidence. Thank you for being not just a coach but a guiding force on and off the court.`,
  },
  {
    name: 'Lassi',
    text: `Kiitos mahtavasta valmennuksesta, Valmentaja Alain! Tennikseen liittyvä intohimosi ja taitosi selittää asiat ymmärrettävästi ovat nostaneet pelitaitojani. Yksilöllinen huomio ja kannustava ilmapiiri lisäävät itseluottamustani. Olet paitsi valmentaja, myös inspiraatio.`,
  },
  {
    name: 'Johannes',
    text: `Out of all the coaches I've had over the years, Alain has by far the best attention to relevant details in my game and which route to take in order for me to improve the most as a player. The passion that Alain has for tennis can't really be matched, it's visible there in every lesson and drill one has with Alain. Top-notch coaching by all means, thank you for being such an excellent coach and an inspiration on the court!`,
  },
];

const gallery = [alainPlay01, alainServe, alainPlay02];

export default function Home() {
  return (
    <main className="font-sans">
      {/* Hero Section */}
      <section className="relative h-[85vh] flex items-center justify-center bg-black overflow-hidden">
        <img src={heroImg} alt="Tennis Hero" className="absolute inset-0 w-full h-full object-cover opacity-60 scale-105" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-transparent to-black/60" />
        <div className="relative z-10 text-center text-white max-w-4xl mx-auto px-6 animate-fade-in">
          <h1 className="text-5xl md:text-7xl font-extrabold mb-6 drop-shadow-2xl leading-tight">
            Elevate Your Game with <span className="text-primary">Elite Coaching</span>
          </h1>
          <p className="text-xl md:text-2xl mb-10 font-medium drop-shadow-lg text-gray-200">
            World-class tennis programs for all ages and skill levels in the heart of Helsinki.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="#contact" className="btn-premium bg-primary hover:bg-primary-dark text-white border-none">
              Book a Lesson
            </a>
            <a href="/courses" className="btn-premium bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white border border-white/30">
              View Programs
            </a>
          </div>
        </div>
      </section>

      {/* Programs Section */}
      <section className="py-24 bg-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-extrabold mb-4 text-navy">Our Programs</h2>
            <div className="w-24 h-1.5 bg-primary mx-auto rounded-full" />
          </div>
          <div className="grid md:grid-cols-3 gap-10">
            {programs.map((p, idx) => (
              <div
                key={p.title}
                className="group bg-white rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-500 overflow-hidden flex flex-col border border-gray-100"
                style={{ animationDelay: `${idx * 150}ms` }}
              >
                <div className="relative h-64 overflow-hidden">
                  <img src={p.img} alt={p.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
                <div className="p-8 flex-1 flex flex-col">
                  <h3 className="text-2xl font-bold mb-3 text-navy group-hover:text-primary transition-colors">{p.title}</h3>
                  <p className="text-gray-600 mb-8 leading-relaxed flex-1">{p.desc}</p>
                  <a
                    href={p.link}
                    className="mt-auto btn-premium bg-gray-50 text-navy hover:bg-primary hover:text-white text-center py-3"
                  >
                    {p.cta}
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Coach Section */}
      <section className="py-24 bg-gray-50 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-1/3 h-full bg-primary/5 -skew-x-12 transform translate-x-1/2" />
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-5 relative">
            <div className="absolute -inset-4 bg-primary/20 rounded-3xl -rotate-3" />
            <img src={alainPortrait} alt="Coach Alain" className="relative rounded-3xl shadow-2xl w-full h-[500px] object-cover" />
          </div>
          <div className="md:col-span-7">
            <h2 className="text-4xl md:text-5xl font-extrabold mb-8 text-navy leading-tight">
              Master the Game with <br />
              <span className="text-primary italic">Coach Alain</span>
            </h2>
            <div className="space-y-6 text-lg text-gray-700 leading-relaxed">
              <p>
                Alain Brouillaud is a certified tennis coach with over <span className="font-bold text-navy">40 years of experience</span>.
                His teaching philosophy transcends basic drills, focusing on the deep synergy between technique, body mechanics, and psychological strategy.
              </p>
              <p>
                Whether you're a beginner finding your first swing or a competitive player refining your serve, Alain's personalized approach ensures every session is a milestone in your tennis journey.
              </p>
            </div>
            <div className="mt-10 flex items-center gap-8">
              <a href="/coach" className="btn-premium bg-navy text-white hover:bg-navy/90">
                Full Bio & Philosophy
              </a>
              <div className="hidden sm:flex flex-col">
                <span className="text-3xl font-black text-primary">40+</span>
                <span className="text-sm font-bold text-gray-500 uppercase tracking-widest">Years Exp.</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Video Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-8 text-primary">Watch Our Coaching in Action</h2>
          <div className="aspect-video w-full rounded-2xl overflow-hidden shadow-lg mx-auto">
            <iframe
              src="https://www.youtube.com/embed/1VpVc9vqkAk"
              title="Tennis Coaching Video"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              className="w-full h-full"
            />
          </div>
          <a href="/videos" className="mt-8 inline-block bg-primary hover:bg-primary-dark text-white font-semibold py-2 px-6 rounded-full transition">See More Videos</a>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24 bg-white relative">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-extrabold mb-4 text-navy">Player Stories</h2>
            <div className="w-24 h-1.5 bg-primary mx-auto rounded-full" />
          </div>
          <div className="grid md:grid-cols-2 gap-10">
            {testimonials.map((t, idx) => (
              <div
                key={t.name}
                className="relative bg-gray-50 rounded-3xl p-10 flex flex-col shadow-sm hover:shadow-xl transition-all duration-300 group"
              >
                <div className="absolute top-8 right-10 text-primary opacity-10 group-hover:opacity-20 transition-opacity">
                  <svg width="60" height="60" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M14.017 21L14.017 18C14.017 14.6863 16.7033 12 20.017 12V6C16.7033 6 14.017 3.31371 14.017 0H8.01705C8.01705 3.31371 5.33076 6 2.01705 6V12C5.33076 12 8.01705 14.6863 8.01705 18V21H14.017Z" />
                  </svg>
                </div>
                <p className="text-lg text-gray-700 italic leading-relaxed relative z-10 mb-8">"{t.text}"</p>
                <div className="mt-auto flex items-center gap-4">
                  <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center text-primary font-bold text-xl">
                    {t.name[0]}
                  </div>
                  <div>
                    <h4 className="text-lg font-bold text-navy">{t.name}</h4>
                    <div className="flex text-yellow-400">
                      {[...Array(5)].map((_, i) => (
                        <svg key={i} className="w-4 h-4 fill-current" viewBox="0 0 20 20">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12 text-primary">Gallery</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {gallery.map((img, i) => (
              <img key={i} src={img} alt={`Gallery ${i + 1}`} className="rounded-2xl shadow-lg w-full h-64 object-cover" />
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-gray-50 relative overflow-hidden">
        <div className="max-w-4xl mx-auto px-6 grid md:grid-cols-2 gap-16">
          <div>
            <h2 className="text-4xl font-extrabold mb-6 text-navy">Get in Touch</h2>
            <p className="text-lg text-gray-600 mb-10 leading-relaxed">
              Ready to take your tennis to the next level? Send a message or reach out directly via WhatsApp for a faster response.
            </p>
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-primary/10 rounded-2xl flex items-center justify-center text-primary">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></svg>
                </div>
                <div>
                  <p className="text-sm font-bold text-gray-400 uppercase tracking-widest">WhatsApp / Tel</p>
                  <a href="tel:+358407339282" className="text-lg font-bold text-navy hover:text-primary transition-colors">040 733 9282</a>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-primary/10 rounded-2xl flex items-center justify-center text-primary">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><polyline points="22,6 12,13 2,6"></polyline></svg>
                </div>
                <div>
                  <p className="text-sm font-bold text-gray-400 uppercase tracking-widest">Email</p>
                  <a href="mailto:alainbrouillaud@mac.com" className="text-lg font-bold text-navy hover:text-primary transition-colors">alainbrouillaud@mac.com</a>
                </div>
              </div>
            </div>
          </div>
          <form className="bg-white rounded-3xl shadow-2xl p-8 flex flex-col gap-6 border border-gray-100">
            <div className="space-y-2">
              <label className="text-sm font-bold text-navy ml-1">Email Address</label>
              <input
                type="email"
                placeholder="you@example.com"
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                required
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-bold text-navy ml-1">Message</label>
              <textarea
                placeholder="Tell us about your level and what you're looking for..."
                rows={4}
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all resize-none"
                required
              />
            </div>
            <button
              type="submit"
              className="btn-premium bg-primary hover:bg-primary-dark text-white w-full shadow-lg shadow-primary/20"
            >
              Send Message
            </button>
          </form>
        </div>
      </section>
    </main>
  );
}
